/**
 * «Карточки Сайт» — колонки таблицы, словари состояний и выгрузка в XLS.
 *
 * Вынесено из страницы по образцу «Каталога ВБ»: колонок стало вдвое больше,
 * и вперемешку с загрузкой они читались бы плохо. Видимость и порядок колонок
 * настраивает пользователь, здесь задан только исходный набор.
 *
 * Статус берётся из `marketplace_cards` как есть. `not_ready` — состояние
 * строки экрана, а не значение статуса карточки: у товара может не быть ни
 * карточки, ни ревизии, и тогда показывать нечего.
 */

import { Badge } from '../../components/ui'
import { ImagePreviewPlaceholder, ImagePreviewThumb } from '../../components/ui/ImagePreviewThumb'
import { generatedVisualUrl } from '../../shared/media/pipelineFiles'
import type { CatalogColumn } from '../marketplace-catalog'
import type { SiteProduct, SiteProductStatus } from './types'

/** Почему карточка не готова к обмену. Порядок причин задаёт бэкенд. */
export const REASON_LABELS: Record<string, string> = {
  no_card: 'Нет карточки сайта или замороженной ревизии',
  no_price: 'Нет цены',
  no_public_image: 'Нет публичной картинки',
  no_category: 'Нет категории',
}

export const reasonLabel = (reason: string | null): string =>
  reason ? (REASON_LABELS[reason] ?? reason) : '—'

/**
 * Подписи состояний карточки. Словарь общий с конвейером: «Создана» здесь
 * означает ровно то же, что на карточках маркетплейсов, — карточка заведена на
 * площадке и получила внешний идентификатор.
 */
export const STATUS_LABELS: Record<SiteProductStatus, string> = {
  draft: 'Черновик',
  creating: 'Создаётся',
  created: 'Создана',
  price_set: 'Цена задана',
  stock_set: 'Остаток задан',
  selling: 'В продаже',
  error: 'Ошибка',
  not_ready: 'Не готова',
}

const STATUS_VARIANTS: Record<SiteProductStatus, 'green' | 'gray' | 'yellow' | 'red'> = {
  draft: 'gray',
  creating: 'gray',
  created: 'green',
  price_set: 'green',
  stock_set: 'green',
  selling: 'green',
  error: 'red',
  not_ready: 'yellow',
}

export const statusBadge = (status: SiteProductStatus, dot = false) => (
  <Badge variant={STATUS_VARIANTS[status] ?? 'gray'} dot={dot}>
    {STATUS_LABELS[status] ?? status}
  </Badge>
)

/**
 * Цена. Незаданная выделяется, а не показывается прочерком: это причина, по
 * которой карточка не уедет, и в списке она должна бросаться в глаза.
 */
export const money = (value: number | null) =>
  value === null ? (
    <span className="text-yellow-700 dark:text-yellow-400">не задана</span>
  ) : (
    <span className="tabular-nums">{value.toLocaleString('ru-RU')} ₽</span>
  )

export const formatMoney = (value: number | null): string =>
  value === null ? '' : `${value.toLocaleString('ru-RU')} ₽`

export const dateTime = (value: string | null): string => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short', timeStyle: 'short' }).format(date)
}

export const seoSourceLabel = (source: SiteProduct['seoSource']): string =>
  source === 'site' ? 'Свой' : source === 'inherited_wb' ? 'От ВБ' : 'Нет'

const seoBadge = (source: SiteProduct['seoSource']) => {
  if (source === 'site') return <Badge variant="green">Свой</Badge>
  if (source === 'inherited_wb') return <Badge variant="gray">От ВБ</Badge>
  return <Badge variant="yellow">Нет</Badge>
}

const dash = <span className="text-gray-300 dark:text-zinc-600">—</span>

function Thumb({ row }: { row: SiteProduct }) {
  if (!row.visualUrl) return <ImagePreviewPlaceholder />
  // Публичный хост отдаёт только полный кадр в полмегабайта, наш маршрут — webp
  // на 7 КБ. Просим второе, публичный адрес держим запасным: файла в томе может
  // не оказаться, и плитка тогда обязана остаться живой.
  if (row.visualPath) {
    return (
      <ImagePreviewThumb
        src={generatedVisualUrl(row.visualPath)}
        fallbackSrc={row.visualUrl}
        thumbnailWidth={160}
        alt={row.pimTitle || row.article}
      />
    )
  }
  return <ImagePreviewThumb src={row.visualUrl} alt={row.pimTitle || row.article} />
}

export const SITE_CARD_COLUMNS: Array<CatalogColumn<SiteProduct>> = [
  {
    key: 'card',
    label: 'Карточка',
    width: 340,
    minWidth: 240,
    sortColumn: 'title',
    locked: true,
    render: (row, { mode }) => (
      <span className="flex min-w-0 items-center gap-2.5">
        {mode === 'standard' && <Thumb row={row} />}
        <span className="min-w-0 flex-1">
          <span
            className="block truncate text-[0.8125rem] text-gray-900 dark:text-zinc-100"
            title={row.pimTitle || undefined}
          >
            {row.pimTitle || 'Без наименования'}
          </span>
          <span className="mt-0.5 flex items-center gap-1.5">
            <span className="shrink-0 font-mono text-xs text-gray-500 dark:text-zinc-400">
              {row.article}
            </span>
            {row.theme && (
              <span className="truncate text-xs text-gray-500 dark:text-zinc-400">
                · {row.theme}
              </span>
            )}
          </span>
        </span>
      </span>
    ),
  },
  {
    key: 'article',
    label: 'Артикул',
    width: 140,
    minWidth: 115,
    sortColumn: 'article',
    render: (row) => <span className="block truncate font-mono text-[0.8125rem]">{row.article}</span>,
  },
  {
    key: 'designCode',
    label: 'Макет',
    width: 130,
    minWidth: 105,
    render: (row) =>
      row.designCode ? (
        <span className="block truncate font-mono text-xs text-gray-600 dark:text-zinc-300">
          {row.designCode}
        </span>
      ) : (
        dash
      ),
  },
  {
    key: 'carrier',
    label: 'Носитель',
    width: 190,
    minWidth: 145,
    sortColumn: 'carrier',
    render: (row) => (
      <span className="block min-w-0">
        <span className="block truncate" title={row.carrier || undefined}>
          {row.carrier || '—'}
        </span>
        <span className="mt-0.5 block font-mono text-2xs text-gray-500 dark:text-zinc-400">
          {row.carrierPrefix || ''}
        </span>
      </span>
    ),
  },
  {
    key: 'theme',
    label: 'Тематика',
    width: 170,
    minWidth: 125,
    sortColumn: 'theme',
    render: (row) =>
      row.theme ? (
        <span className="block truncate" title={row.theme}>
          {row.theme}
        </span>
      ) : (
        dash
      ),
  },
  {
    key: 'category',
    label: 'Категория макета',
    width: 165,
    minWidth: 125,
    render: (row) =>
      row.category ? (
        <span className="block truncate text-gray-700 dark:text-zinc-300" title={row.category}>
          {row.category}
        </span>
      ) : (
        dash
      ),
  },
  {
    key: 'designType',
    label: 'Тип макета',
    width: 140,
    minWidth: 110,
    render: (row) =>
      row.designType ? (
        <span className="block truncate text-gray-700 dark:text-zinc-300">{row.designType}</span>
      ) : (
        dash
      ),
  },
  {
    key: 'price',
    label: 'Цена',
    width: 115,
    minWidth: 95,
    sortColumn: 'price',
    align: 'right',
    render: (row) => money(row.price),
  },
  {
    key: 'hasVisual',
    label: 'Визуал',
    width: 105,
    minWidth: 90,
    align: 'center',
    render: (row) =>
      row.hasVisual ? <Badge variant="green">Есть</Badge> : <Badge variant="yellow">Нет</Badge>,
  },
  {
    key: 'seoSource',
    label: 'Текст',
    width: 115,
    minWidth: 95,
    align: 'center',
    render: (row) => seoBadge(row.seoSource),
  },
  {
    key: 'status',
    label: 'Статус',
    width: 140,
    minWidth: 115,
    align: 'center',
    render: (row) => statusBadge(row.status, true),
  },
  {
    key: 'externalId',
    label: 'ID на витрине',
    width: 130,
    minWidth: 105,
    render: (row) =>
      row.externalId ? (
        <span className="block truncate font-mono text-xs text-indigo-700 dark:text-indigo-300">
          {row.externalId}
        </span>
      ) : (
        dash
      ),
  },
  {
    key: 'reason',
    label: 'Причина',
    width: 210,
    minWidth: 150,
    render: (row) =>
      row.reason ? <span className="whitespace-normal">{reasonLabel(row.reason)}</span> : dash,
  },
  {
    key: 'lastRunAt',
    label: 'Последний прогон',
    width: 155,
    minWidth: 125,
    sortColumn: 'last_run_at',
    render: (row) => (
      <span className="block whitespace-nowrap tabular-nums text-gray-500 dark:text-zinc-400">
        {dateTime(row.lastRunAt)}
      </span>
    ),
  },
  {
    key: 'updatedAt',
    label: 'Обновлён',
    width: 150,
    minWidth: 125,
    sortColumn: 'updated_at',
    render: (row) => (
      <span className="block whitespace-nowrap tabular-nums text-gray-500 dark:text-zinc-400">
        {dateTime(row.updatedAt)}
      </span>
    ),
  },
]

/** Колонки, видимые до того как пользователь настроит своё */
export const SITE_CARD_DEFAULT_VISIBLE = [
  'card',
  'carrier',
  'price',
  'hasVisual',
  'seoSource',
  'status',
  'reason',
  'updatedAt',
]

/** Колонки выгрузки в XLS — понятия сайта, а не внутренние имена полей */
export const SITE_CARD_XLS_COLUMNS = [
  { key: 'article', header: 'Артикул', value: (row: SiteProduct) => row.article },
  { key: 'designCode', header: 'Макет', value: (row: SiteProduct) => row.designCode || '' },
  { key: 'pimTitle', header: 'Наименование ПИМ', value: (row: SiteProduct) => row.pimTitle || '' },
  { key: 'carrier', header: 'Носитель', value: (row: SiteProduct) => row.carrier || '' },
  { key: 'carrierPrefix', header: 'Префикс', value: (row: SiteProduct) => row.carrierPrefix || '' },
  { key: 'theme', header: 'Тематика', value: (row: SiteProduct) => row.theme || '' },
  { key: 'category', header: 'Категория макета', value: (row: SiteProduct) => row.category || '' },
  { key: 'designType', header: 'Тип макета', value: (row: SiteProduct) => row.designType || '' },
  { key: 'price', header: 'Цена', value: (row: SiteProduct) => formatMoney(row.price) },
  { key: 'hasVisual', header: 'Визуал', value: (row: SiteProduct) => (row.hasVisual ? 'Есть' : 'Нет') },
  { key: 'seoSource', header: 'Текст', value: (row: SiteProduct) => seoSourceLabel(row.seoSource) },
  {
    key: 'status',
    header: 'Статус',
    value: (row: SiteProduct) => STATUS_LABELS[row.status] ?? row.status,
  },
  { key: 'externalId', header: 'ID на витрине', value: (row: SiteProduct) => row.externalId || '' },
  { key: 'reason', header: 'Причина', value: (row: SiteProduct) => reasonLabel(row.reason) },
  { key: 'lastRunAt', header: 'Последний прогон', value: (row: SiteProduct) => dateTime(row.lastRunAt) },
  { key: 'updatedAt', header: 'Обновлён', value: (row: SiteProduct) => dateTime(row.updatedAt) },
]
